import { type SupportedLocales } from "@app/shared/utils/locales";
import { useUser } from "@app/store/userStore";
import { Skeleton } from "@design-system/DataDisplay/Skeleton";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { questionnaireLocaleAtom } from "../../ctx";
import { useQuestionnaireHeaderData } from "../../data";
import "../../i18n";
import { useQuestionnaireHeaderTranslations } from "./i18n";
import { QuestionnaireLocaleDropdown } from "./QuestionnaireLanguageDropdown";

export const QuestionnaireHeader = ({
    questionnaireId,
    isFramework = false,
    onEdit,
}: {
    questionnaireId?: string;
    isFramework?: boolean;
    onEdit?: () => void;
}) => {
    const { t, keys } = useQuestionnaireHeaderTranslations();
    const { questionnaireHeaderData } =
        useQuestionnaireHeaderData(questionnaireId);

    const creator = useUser(questionnaireHeaderData?.creatorId ?? undefined);
    const creatorName =
        creator?.firstName || creator?.lastName
            ? `${creator?.firstName} ${creator?.lastName}`
            : "-";

    const setCurrentLocale = useSetAtom(questionnaireLocaleAtom);

    useEffect(() => {
        setCurrentLocale(
            questionnaireHeaderData?.locale?.default as SupportedLocales,
        );
    }, [questionnaireHeaderData?.locale?.default]);

    if (!questionnaireHeaderData)
        return (
            <FlexCol gap="2">
                <FlexRow alignItems="center" gap="3" py="1.5">
                    <Skeleton w="6" h="6" />
                    <Skeleton className="w-[300px]" h="6" />
                </FlexRow>
                <Skeleton my="1.5" className="w-[200px]" h="2" />
            </FlexCol>
        );

    return (
        <FlexCol gap="2">
            <FlexRow alignItems="center" justifyContent="between">
                <FlexRow alignItems="center" gap="4">
                    <FlexRow alignItems="center" gap="3">
                        <Icon
                            name={
                                isFramework ? "clipboardCheck" : "clipboardList"
                            }
                            size="lg"
                        />
                        <Text as="h1" variant="hero6">
                            {questionnaireHeaderData.name}
                        </Text>
                    </FlexRow>
                    {onEdit && (
                        <Button variant="textLink" size="sm" onClick={onEdit}>
                            <Button.Icon name="edit" />
                            {t(keys.edit)}
                        </Button>
                    )}
                </FlexRow>
                <QuestionnaireLocaleDropdown
                    questionnaireId={questionnaireId}
                    isChip
                />
            </FlexRow>
            <FlexCol>
                <Text color="secondary" variant="body-sm">
                    {t(keys.subtitle_created_by)}{" "}
                    <Text as="span" color="primary" variant="body-sm-bold">
                        {creatorName}
                    </Text>
                </Text>
                {(!!questionnaireHeaderData.companyName ||
                    !!questionnaireHeaderData.requesterEmail) && (
                    <Text color="secondary" variant="body-sm">
                        {t(keys.subtitle_requested_by)}
                        <Text as="span" color="primary" variant="body-sm-bold">
                            {questionnaireHeaderData.companyName ?? "-"}
                        </Text>
                        {questionnaireHeaderData.requesterEmail && (
                            <Text as="span" color="secondary" variant="body-sm">
                                {" "}
                                ({questionnaireHeaderData.requesterEmail})
                            </Text>
                        )}
                    </Text>
                )}
            </FlexCol>
        </FlexCol>
    );
};
